// Right-panel inspector. Generates param controls from the selected layer's spec.

import { effect } from '@/core/signals';
import {
  selectedLayer,
  state,
  beginEdit,
  endEdit,
  updateLayer,
  removeLayer,
} from '@/core/state';
import { getLayerSpec, type ParamSchema, type ParamSchemaField } from '@/core/pipeline';
import type { Layer, BlendMode } from '@/core/types';
import { el, on, replaceChildren, formatNum } from '@/lib/dom';
import { ICON } from './icons';
import { slider } from './controls/slider';
import { selectControl } from './controls/select';
import { colorControl } from './controls/colorSwatch';
import { toggleControl } from './controls/toggle';
import { seedControl } from './controls/seedField';

const BLENDS: BlendMode[] = [
  'source-over', 'multiply', 'screen', 'overlay', 'darken', 'lighten',
  'color-dodge', 'color-burn', 'hard-light', 'soft-light',
  'difference', 'exclusion', 'hue', 'saturation', 'color', 'luminosity',
];

export function mountInspector(host: HTMLElement): () => void {
  return effect(() => {
    const layer = selectedLayer.get();
    void state.get(); // re-render on any state tick
    if (!layer) {
      replaceChildren(host, emptyState());
      return;
    }
    replaceChildren(host, buildPanel(layer));
  });
}

function emptyState(): HTMLElement {
  return el('div', { class: 'ins-empty' }, [
    el('div', { class: 'ins-empty-title' }, ['No layer selected']),
    el('div', { class: 'ins-empty-hint' }, ['Add or pick a layer from the left panel.']),
  ]);
}

function buildPanel(layer: Layer): HTMLElement {
  const spec = getLayerSpec(layer.kind);
  const header = el('div', { class: 'ins-head' }, [
    el('div', { class: 'ins-head-title' }, [
      el('span', { class: 'ins-kind' }, [spec?.label ?? layer.kind]),
      el('span', { class: 'ins-cat' }, [spec?.category ?? '']),
    ]),
    deleteButton(layer),
  ]);

  const sectionParams = paramSection(layer, spec?.schema ?? {});
  const sectionBlend = blendSection(layer);
  const sectionSeed = seedSection(layer);

  return el('div', { class: 'ins' }, [header, sectionParams, sectionBlend, sectionSeed]);
}

function deleteButton(layer: Layer): HTMLElement {
  const btn = el('button', {
    class: 'btn-icon',
    ariaLabel: 'Remove layer',
    title: 'Remove layer',
    innerHTML: ICON.trash,
  });
  on(btn, 'click', () => removeLayer(layer.id));
  return btn;
}

function paramSection(layer: Layer, schema: ParamSchema): HTMLElement {
  const section = el('div', { class: 'ins-sec' }, [
    el('div', { class: 'ins-sec-title' }, ['Parameters']),
  ]);

  if (layer.kind === 'image') {
    section.appendChild(imagePicker(layer));
  }

  const keys = Object.keys(schema);
  if (keys.length === 0 && layer.kind !== 'image') {
    section.appendChild(el('div', { class: 'ins-empty-hint' }, ['No editable parameters.']));
    return section;
  }

  for (const key of keys) {
    const field = schema[key] as ParamSchemaField;
    if (layer.kind === 'image' && key === 'src') continue; // handled by picker
    const ctrl = controlForField(layer, key, field);
    if (ctrl) section.appendChild(ctrl);
  }

  return section;
}

function imagePicker(layer: Layer): HTMLElement {
  const btn = el('button', { class: 'btn modal-btn' }, ['Choose image…']);
  btn.style.width = '100%';
  on(btn, 'click', async () => {
    const { openFilePicker } = await import('@/lib/imageImport');
    openFilePicker();
  });
  const hint = el('div', { class: 'ins-empty-hint' }, [
    'or drop / paste an image anywhere',
  ]);
  hint.style.marginTop = '6px';
  void layer;
  return el('div', {}, [btn, hint]);
}

function controlForField(
  layer: Layer,
  key: string,
  field: ParamSchemaField,
): HTMLElement | null {
  const current = layer.params[key];
  switch (field.type) {
    case 'number':
    case 'int': {
      const value = typeof current === 'number' ? current : field.default;
      const opts: Parameters<typeof slider>[0] = {
        label: field.label ?? key,
        min: field.min ?? 0,
        max: field.max ?? 100,
        step: field.step ?? (field.type === 'int' ? 1 : 0.01),
        value,
        onInput: (v) =>
          updateLayer(layer.id, {
            params: { [key]: field.type === 'int' ? Math.round(v) : v },
          }),
        onBegin: beginEdit,
        onEnd: endEdit,
      };
      if (field.type === 'int') opts.format = (v) => Math.round(v).toString();
      return slider(opts);
    }
    case 'color': {
      const value = typeof current === 'string' ? current : field.default;
      return colorControl({
        label: field.label ?? key,
        value,
        onChange: (v) => updateLayer(layer.id, { params: { [key]: v } }),
        onBegin: beginEdit,
        onEnd: endEdit,
      });
    }
    case 'bool': {
      const value = typeof current === 'boolean' ? current : field.default;
      return toggleControl({
        label: field.label ?? key,
        value,
        onChange: (v) => updateLayer(layer.id, { params: { [key]: v } }),
      });
    }
    case 'select': {
      const value =
        typeof current === 'string' && field.options.includes(current)
          ? current
          : field.default;
      return selectControl({
        label: field.label ?? key,
        value,
        options: field.options,
        onChange: (v) => updateLayer(layer.id, { params: { [key]: v } }),
      });
    }
    case 'palette': {
      // Treat palette as a select for now; richer picker later.
      const value = typeof current === 'string' ? current : field.default;
      return selectControl({
        label: field.label ?? key,
        value,
        options: [value],
        onChange: (v) => updateLayer(layer.id, { params: { [key]: v } }),
      });
    }
    case 'image':
      return null; // handled by image generator's own UI hooks
    default:
      return null;
  }
}

// Image-layer extra controls: a "Choose image…" picker rendered into the
// Parameters section by `paramSection` when kind is 'image'.

function blendSection(layer: Layer): HTMLElement {
  return el('div', { class: 'ins-sec' }, [
    el('div', { class: 'ins-sec-title' }, ['Blend']),
    selectControl({
      label: 'Mode',
      value: layer.blend,
      options: BLENDS,
      onChange: (v) => updateLayer(layer.id, { blend: v as BlendMode }),
    }),
    slider({
      label: 'Opacity',
      min: 0,
      max: 1,
      step: 0.01,
      value: layer.opacity,
      format: (v) => `${Math.round(v * 100)}%`,
      onInput: (v) => updateLayer(layer.id, { opacity: v }),
      onBegin: beginEdit,
      onEnd: endEdit,
    }),
  ]);
}

function seedSection(layer: Layer): HTMLElement {
  return el('div', { class: 'ins-sec' }, [
    el('div', { class: 'ins-sec-title' }, ['Identity']),
    seedControl({
      label: 'Seed',
      value: layer.seed,
      onChange: (v) => updateLayer(layer.id, { seed: v }),
    }),
    el('div', { class: 'ins-id mono' }, [`#${layer.id}`]),
  ]);
}

// Suppress unused-imports lint: formatNum is intentionally available for future use.
void formatNum;
